import { useState, useEffect, useCallback, useRef } from 'react';
import { useDebounce } from './useDebounce';
import {
  searchProducts,
  getAutocomplete,
  getDefaults,
  getProductsByIds,
  trackSessionClick,
  getApiBase,
} from '../utils/api';
import { ga4Search, ga4SelectItem, ga4Event } from '../utils/ga4';
import { saveResultsSnapshot, getSessionStorageSafe } from '../utils/searchHistory';
import {
  addRecentProductId,
  getLocalStorageSafe,
  loadRecentProductIds,
  MAX_RECENT_PRODUCTS,
  saveRecentProductIds,
} from '../utils/recentProducts';
import type { InitialRestore } from './useSearchHistory';
import type {
  SearchResponse,
  DefaultsResponse,
  SearchFilters,
  Product,
} from '../utils/types';

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const SEARCH_DEBOUNCE_MS = 200;
const AUTOCOMPLETE_DEBOUNCE_MS = 50;
const MAX_RECENT_SEARCHES = 8;
const RECENT_SEARCHES_KEY = 'botilia_recent_searches';
const SIDEBAR_VISIBILITY_KEY = 'botilia_show_sidebar';
const SESSION_ID_KEY = 'botilia_session_id';

/* ------------------------------------------------------------------ */
/*  Local storage helpers                                              */
/* ------------------------------------------------------------------ */

function recentSearchesKey(lang?: string): string {
  return lang ? `${RECENT_SEARCHES_KEY}_${lang}` : RECENT_SEARCHES_KEY;
}

function loadRecentSearches(lang?: string): string[] {
  try {
    const raw = localStorage.getItem(recentSearchesKey(lang));
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.slice(0, MAX_RECENT_SEARCHES);
    }
  } catch {
    /* ignore */
  }
  return [];
}

function saveRecentSearches(searches: string[], lang?: string): void {
  try {
    localStorage.setItem(
      recentSearchesKey(lang),
      JSON.stringify(searches.slice(0, MAX_RECENT_SEARCHES)),
    );
  } catch {
    /* ignore */
  }
}

function loadSidebarVisibility(): boolean {
  try {
    const raw = localStorage.getItem(SIDEBAR_VISIBILITY_KEY);
    // Default to true on desktop (window.innerWidth > 768)
    if (raw === null) {
      return typeof window !== 'undefined' && window.innerWidth > 768;
    }
    return raw === 'true';
  } catch {
    return true;
  }
}

function saveSidebarVisibility(visible: boolean): void {
  try {
    localStorage.setItem(SIDEBAR_VISIBILITY_KEY, String(visible));
  } catch {
    /* ignore */
  }
}

/** UUID v4 — crypto.randomUUID only exists in secure contexts (https/localhost),
 *  so embeds served over plain http (e.g. LAN-IP testing) need a fallback. */
function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Get or create a session ID (persists in sessionStorage for the tab lifetime). */
export function getSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_ID_KEY);
    if (!id) {
      id = generateId();
      sessionStorage.setItem(SESSION_ID_KEY, id);
    }
    return id;
  } catch {
    // Fallback if sessionStorage is blocked
    return generateId();
  }
}

/* ------------------------------------------------------------------ */
/*  Hook return type                                                   */
/* ------------------------------------------------------------------ */

export interface UseSearchReturn {
  /* State */
  query: string;
  results: SearchResponse | null;
  defaults: DefaultsResponse | null;
  autocomplete: string[];
  isLoading: boolean;
  isLoadingMore: boolean;
  isLoadingDefaults: boolean;
  error: string | null;
  isOpen: boolean;
  activeIndex: number;
  recentSearches: string[];
  recentProducts: Product[];
  filters: SearchFilters;
  showAutocomplete: boolean;
  showResults: boolean;
  showDefaults: boolean;
  showNoResults: boolean;
  showSidebar: boolean;
  hasMore: boolean;
  currentPage: number;
  isAIThinking: boolean;

  /* Actions */
  setQuery: (q: string) => void;
  setFilters: (f: SearchFilters) => void;
  clearFilters: () => void;
  applyBrowseFilters: (filters: Partial<SearchFilters>) => void;
  open: () => void;
  close: () => void;
  selectSuggestion: (suggestion: string) => void;
  clearQuery: () => void;
  clearRecentSearches: () => void;
  trackProductClick: (product: Product) => void;
  handleKeyDown: (e: React.KeyboardEvent) => void;
  toggleSidebar: () => void;
  loadMore: () => void;
  /** "Search instead for…": repeat this search without spell correction. */
  searchExact: () => void;
  /** Reopen with a previous search (browser history restore). `loadedCount`
   *  products are fetched in one go so the scroll position can be restored. */
  restoreSearchState: (state: RestoreInput) => void;
}

/* ------------------------------------------------------------------ */
/*  The hook                                                           */
/* ------------------------------------------------------------------ */

const PAGE_SIZE = 24;
/** Max products fetched in one request when restoring page depth (backend caps limit at 100). */
const MAX_RESTORE_LIMIT = 96;

function searchKey(q: string, filters: SearchFilters): string {
  return `${q}|${JSON.stringify(filters)}`;
}

/** First-request limit needed to re-fetch `loadedCount` products in one go
 *  (null when a normal page suffices). */
function restoreLimitFor(q: string, filters: SearchFilters, loadedCount: number): { key: string; limit: number } | null {
  const pageSize = filters.limit || PAGE_SIZE;
  const pages = Math.ceil(loadedCount / pageSize);
  const limit = Math.min(Math.max(pages, 1) * pageSize, MAX_RESTORE_LIMIT);
  return limit > pageSize ? { key: searchKey(q, filters), limit } : null;
}

interface RestoreInput {
  query: string;
  filters: SearchFilters;
  loadedCount: number;
  snapshot?: { results: SearchResponse; ts: number } | null;
}

/**
 * @param initialRestore Browser-history restore (Back from a product page):
 *   seeds query/filters/results synchronously so the FIRST render already
 *   shows the previous results — no debounce, no skeleton, no empty frame.
 */
export function useSearch(lang?: string, initialRestore?: InitialRestore | null): UseSearchReturn {
  /* ---- Initial restore seed (derived once, before any state) ---- */
  const [initialSeed] = useState(() => {
    if (!initialRestore) return null;
    const q = initialRestore.query.trim();
    const f = initialRestore.filters;
    const snap = initialRestore.snapshot ?? null;
    const loaded = snap ? snap.results.products.length : initialRestore.loadedCount;
    const restore = restoreLimitFor(q, f, loaded);
    const limit = restore?.limit ?? (f.limit || PAGE_SIZE);
    const key = searchKey(q, f);
    return { restore, snap, key, cacheKey: `${key}|off=0|lim=${limit}` };
  });

  /* ---- Core state ---- */
  const [query, setQueryRaw] = useState(initialRestore?.query ?? '');
  const [results, setResults] = useState<SearchResponse | null>(initialSeed?.snap?.results ?? null);
  const [defaults, setDefaults] = useState<DefaultsResponse | null>(null);
  const [autocomplete, setAutocomplete] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLoadingDefaults, setIsLoadingDefaults] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(!!initialRestore);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [recentSearches, setRecentSearches] = useState<string[]>(
    () => loadRecentSearches(lang),
  );
  const [recentProductIds, setRecentProductIds] = useState<string[]>(
    () => loadRecentProductIds(getLocalStorageSafe()),
  );
  const [recentProducts, setRecentProducts] = useState<Product[]>([]);
  const [filters, setFiltersRaw] = useState<SearchFilters>(initialRestore?.filters ?? {});
  const [showSidebar, setShowSidebar] = useState<boolean>(loadSidebarVisibility);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(() => {
    const r = initialSeed?.snap?.results;
    return r ? r.products.length < r.total : false;
  });
  const [accumulatedProducts, setAccumulatedProducts] = useState<SearchResponse['products']>(
    () => initialSeed?.snap?.results.products ?? [],
  );

  const debouncedAutocomplete = useDebounce(query, AUTOCOMPLETE_DEBOUNCE_MS);
  /* Search debounce is hand-rolled (not useDebounce) so a history restore can
   * set the settled value directly instead of waiting SEARCH_DEBOUNCE_MS. */
  const [debouncedQuery, setDebouncedQuery] = useState(initialRestore?.query ?? '');
  const [exactMatch, setExactMatch] = useState(false);
  useEffect(() => {
    if (query === debouncedQuery) return;
    const timer = setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Abort controller for in-flight requests */
  const abortRef = useRef<AbortController | null>(null);
  const autocompleteAbortRef = useRef<AbortController | null>(null);

  /* GA4: fire search events once per settled page-1 query (both the cache
   * path and the network path deliver results; revalidation must not re-fire) */
  const ga4FiredKeyRef = useRef(initialSeed?.snap ? initialSeed.cacheKey : '');

  /* Abandonment tracking — reset on each new search, set on product click.
   * A history restore starts "clicked": returning from a result isn't abandonment. */
  const hasClickedRef = useRef(!!initialRestore);
  const lastSearchQueryRef = useRef(initialRestore?.query.trim() ?? '');
  const lastSearchResultsRef = useRef(initialSeed?.snap?.results.total ?? 0);

  /* History restore: fetch N pages' worth of products on the first request so
   * the restored scroll position lands on results that exist. Keyed by the
   * query+filters it applies to, so a new search falls back to PAGE_SIZE. */
  const restoreRef = useRef<{ key: string; limit: number } | null>(initialSeed?.restore ?? null);
  /* searchKey of the (query, filters) the current `results` were fetched for.
   * `filters` changes a render before the new response lands, so anything
   * that persists results must check this instead of trusting state. */
  const resultsKeyRef = useRef<string>(initialSeed?.snap ? initialSeed.key : '');
  /* Products loaded so far — the offset for the next page (not page*size,
   * since a restored first page may span several pages). */
  const accumulatedLenRef = useRef(0);
  accumulatedLenRef.current = accumulatedProducts.length;

  /* Client-side caches */
  const autocompleteCacheRef = useRef<Map<string, { data: string[]; ts: number }>>(new Map());
  const [seededSearchCache] = useState(() => {
    const m = new Map<string, { data: SearchResponse; ts: number }>();
    // The snapshot keeps its original timestamp: if it's older than the cache
    // TTL the fetch effect revalidates quietly behind the already-painted results.
    if (initialSeed?.snap) m.set(initialSeed.cacheKey, { data: initialSeed.snap.results, ts: initialSeed.snap.ts });
    return m;
  });
  const searchCacheRef = useRef<Map<string, { data: SearchResponse; ts: number }>>(seededSearchCache);
  const AC_CACHE_TTL = 60_000; // 1 minute
  const SEARCH_CACHE_TTL = 120_000; // 2 minutes
  const AC_CACHE_MAX = 200;
  const SEARCH_CACHE_MAX = 30;

  /* ---- Derived state ---- */
  const trimmedQuery = query.trim();
  const hasDebouncedQuery = debouncedQuery.trim().length > 0;
  const hasActiveFilters = !!(
    filters.category || filters.type || filters.country ||
    filters.region || filters.grape || filters.producer ||
    filters.minPrice !== undefined || filters.maxPrice !== undefined ||
    filters.inStock
  );
  const hasResults =
    results !== null &&
    (results.products.length > 0 ||
      results.articles.length > 0 ||
      results.pages.length > 0);

  const showAutocomplete =
    isOpen && trimmedQuery.length > 0 && autocomplete.length > 0;
  const showResults = isOpen && (hasDebouncedQuery || hasActiveFilters) && hasResults;
  const showNoResults =
    isOpen &&
    (hasDebouncedQuery || hasActiveFilters) &&
    results !== null &&
    !hasResults &&
    !isLoading;
  const showDefaults =
    isOpen && trimmedQuery.length === 0 && !hasActiveFilters && defaults !== null;

  /* ---- Reload recent searches when lang changes ---- */
  useEffect(() => {
    setRecentSearches(loadRecentSearches(lang));
  }, [lang]);

  /* ---- Recently viewed: ids live in the browser, details come in this language ---- */
  useEffect(() => {
    if (recentProductIds.length === 0) {
      setRecentProducts([]);
      return;
    }
    let cancelled = false;
    getProductsByIds(recentProductIds, lang)
      .then((products) => {
        if (!cancelled) setRecentProducts(products);
      })
      .catch(() => {
        /* Non-critical; the section just stays as it is */
      });
    return () => {
      cancelled = true;
    };
  }, [recentProductIds, lang]);

  /* ---- Fetch defaults on mount ---- */
  useEffect(() => {
    let cancelled = false;
    setIsLoadingDefaults(true);
    getDefaults(lang)
      .then((data) => {
        if (!cancelled) setDefaults(data);
      })
      .catch(() => {
        /* Defaults are non-critical; fail silently */
      })
      .finally(() => {
        if (!cancelled) setIsLoadingDefaults(false);
      });
    return () => {
      cancelled = true;
    };
  }, [lang]);

  /* ---- Fetch autocomplete on keystroke (debounced 50ms) ---- */
  useEffect(() => {
    const trimmed = debouncedAutocomplete.trim();
    if (trimmed.length === 0) {
      setAutocomplete([]);
      return;
    }

    // Check client-side cache first
    const cached = autocompleteCacheRef.current.get(trimmed);
    if (cached && Date.now() - cached.ts < AC_CACHE_TTL) {
      setAutocomplete(cached.data);
      setActiveIndex(-1);
      return;
    }

    // Cancel any in-flight autocomplete request
    if (autocompleteAbortRef.current) autocompleteAbortRef.current.abort();
    const controller = new AbortController();
    autocompleteAbortRef.current = controller;

    getAutocomplete(trimmed, 7, controller.signal, lang)
      .then((data) => {
        if (!controller.signal.aborted) {
          setAutocomplete(data);
          setActiveIndex(-1);
          // Cache the result
          autocompleteCacheRef.current.set(trimmed, { data, ts: Date.now() });
          if (autocompleteCacheRef.current.size > AC_CACHE_MAX) {
            const firstKey = autocompleteCacheRef.current.keys().next().value;
            if (firstKey !== undefined) autocompleteCacheRef.current.delete(firstKey);
          }
        }
      })
      .catch((err) => {
        if (
          !controller.signal.aborted &&
          !(err instanceof DOMException && err.name === 'AbortError')
        ) {
          setAutocomplete([]);
        }
      });

    return () => {
      controller.abort();
    };
  }, [debouncedAutocomplete]);

  /* ---- Reset pagination when query or filters change ---- */
  useEffect(() => {
    setCurrentPage(1);
    setAccumulatedProducts([]);
    setHasMore(false);
  }, [debouncedQuery, exactMatch, filters.category, filters.type, filters.country, filters.region, filters.grape, filters.producer, filters.body, filters.acidity, filters.tannins, filters.fruitiness, filters.minPrice, filters.maxPrice, filters.inStock, filters.sort]);

  /* ---- Back/forward-cache (bfcache) handling ----
   * Clicking a result navigates the tab away, which kills in-flight fetches —
   * Safari reports that as `TypeError: Load failed` (not an AbortError), so
   * the widget would store an error state, get snapshotted into the bfcache
   * with the error showing, and pressing Back restored the frozen error.
   * Two guards: don't surface fetch failures while the page is being left,
   * and re-run the current search when the page is restored from bfcache. */
  const [refetchTick, setRefetchTick] = useState(0);
  const navigatingAwayRef = useRef(false);
  useEffect(() => {
    const onPageHide = () => { navigatingAwayRef.current = true; };
    const onPageShow = (e: PageTransitionEvent) => {
      navigatingAwayRef.current = false;
      if (e.persisted) {
        setError(null);
        setRefetchTick((t) => t + 1);
      }
    };
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);

  /* ---- Fetch search results on debounced query / filter change ---- */
  useEffect(() => {
    const q = debouncedQuery.trim();
    // Allow search when either there's a text query OR active filters (browse mode)
    const filtersActive = !!(
      filters.category || filters.type || filters.country ||
      filters.region || filters.grape || filters.producer ||
      filters.body || filters.acidity || filters.tannins || filters.fruitiness ||
      filters.minPrice !== undefined || filters.maxPrice !== undefined ||
      filters.inStock
    );
    if (q.length === 0 && !filtersActive) {
      setResults(null);
      setError(null);
      setAccumulatedProducts([]);
      setHasMore(false);
      return;
    }

    // Use page size from filters.limit or default PAGE_SIZE
    const pageSize = filters.limit || PAGE_SIZE;
    const key = searchKey(q, filters);
    const pageOneLimit = restoreRef.current?.key === key ? restoreRef.current.limit : pageSize;
    const limit = currentPage === 1 ? pageOneLimit : pageSize;
    const offset = currentPage === 1 ? 0 : accumulatedLenRef.current;
    const cacheKey = `${key}|off=${offset}|lim=${limit}${exactMatch ? '|exact' : ''}`;

    // Reset abandonment tracking for each new page-1 search
    if (currentPage === 1) {
      hasClickedRef.current = false;
      lastSearchQueryRef.current = q;
      lastSearchResultsRef.current = 0; // will be updated when results arrive
    }

    // Check client-side search cache
    const cached = searchCacheRef.current.get(cacheKey);
    // Stale-while-revalidate: when we already painted a cached page, the
    // refresh must not swap the results for a skeleton (that is the flicker
    // users see coming Back from a product page).
    const servedFromCache = !!cached;
    if (cached) {
      if (currentPage === 1) {
        lastSearchResultsRef.current = cached.data.total;
        resultsKeyRef.current = key;
        setResults(cached.data);
        setAccumulatedProducts(cached.data.products);
        if (q && ga4FiredKeyRef.current !== cacheKey) {
          ga4FiredKeyRef.current = cacheKey;
          ga4Search(q, cached.data.total, cached.data.products);
        }
      } else {
        // Append to accumulated products using callback form
        setAccumulatedProducts(prev => {
          const newProducts = [...prev, ...cached.data.products];
          setResults({
            ...cached.data,
            products: newProducts,
          });
          return newProducts;
        });
      }
      // More exist only if what we've loaded so far is fewer than the total.
      // (Comparing page size to pageSize wrongly shows "load more" when the
      //  total is an exact multiple of the page size, e.g. exactly 24 results.)
      setHasMore(offset + cached.data.products.length < cached.data.total);
      setIsLoading(false);
      setIsLoadingMore(false);
      // If still fresh, skip network call
      if (Date.now() - cached.ts < SEARCH_CACHE_TTL) {
        return;
      }
      // Otherwise fall through to revalidate in background
    }

    /* Cancel previous request */
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    if (currentPage === 1) {
      if (!servedFromCache) setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }
    setError(null);

    searchProducts({
      q,
      category: filters.category,
      type: filters.type,
      country: filters.country,
      region: filters.region,
      grape: filters.grape,
      producer: filters.producer,
      body: filters.body,
      acidity: filters.acidity,
      tannins: filters.tannins,
      fruitiness: filters.fruitiness,
      minPrice: filters.minPrice,
      maxPrice: filters.maxPrice,
      inStock: filters.inStock,
      sort: filters.sort,
      limit,
      offset,
      sessionId: getSessionId(),
      lang,
      exact: exactMatch || undefined,
    })
      .then((data) => {
        if (!controller.signal.aborted) {
          if (currentPage === 1) {
            lastSearchResultsRef.current = data.total;
            resultsKeyRef.current = key;
            setResults(data);
            setAccumulatedProducts(data.products);
            if (q && ga4FiredKeyRef.current !== cacheKey) {
              ga4FiredKeyRef.current = cacheKey;
              ga4Search(q, data.total, data.products);
            }
          } else {
            // Append new products using callback form to avoid stale closure
            setAccumulatedProducts(prev => {
              const newProducts = [...prev, ...data.products];
              setResults({
                ...data,
                products: newProducts,
              });
              return newProducts;
            });
          }
          setHasMore(offset + data.products.length < data.total);
          // Keep autocomplete visible — cleared when user selects a suggestion
          setActiveIndex(-1);
          // Cache the result
          searchCacheRef.current.set(cacheKey, { data, ts: Date.now() });
          if (searchCacheRef.current.size > SEARCH_CACHE_MAX) {
            const firstKey = searchCacheRef.current.keys().next().value;
            if (firstKey !== undefined) searchCacheRef.current.delete(firstKey);
          }
        }
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          if (err instanceof DOMException && err.name === 'AbortError') return;
          // Fetch killed by the tab navigating to a clicked result — not a
          // real failure; pageshow refetches if the user comes Back.
          if (navigatingAwayRef.current) return;
          // A failed background revalidation keeps the cached page on screen.
          if (servedFromCache && currentPage === 1) return;
          setError(
            err instanceof Error
              ? err.message
              : 'Search error',
          );
          if (currentPage === 1) {
            setResults(null);
            setAccumulatedProducts([]);
          }
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      });

    return () => {
      controller.abort();
    };
  }, [debouncedQuery, filters, currentPage, refetchTick, exactMatch]);

  /* ---- Persist the rendered results so a Back-button return can paint
   * them in its first frame (see useSearchHistory). Guarded so a response
   * that doesn't belong to the current query can't be saved under it. ---- */
  useEffect(() => {
    if (!results) return;
    const q = debouncedQuery.trim();
    // Only persist results that were fetched for exactly this query+filters.
    // Right after a filter click, `filters` is already new while `results`
    // is still the previous response; saving that would restore the old
    // products under the new filter chips.
    if (resultsKeyRef.current !== searchKey(q, filters)) return;
    saveResultsSnapshot(getSessionStorageSafe(), { query: q, filters, results });
  }, [results, debouncedQuery, filters]);

  /* ---- AI context polling: re-fetch when AI is still processing ---- */
  const aiPollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const aiPollCountRef = useRef(0);
  // Latest page without re-running the poll effect: the poll response only
  // covers offset 0, so it may replace results only while still on page 1.
  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;

  const aiSource = results?.aiContext?.source;
  const isAIThinking = aiSource === 'pending' && !!debouncedQuery.trim();

  useEffect(() => {
    // Clear any existing poll timer
    if (aiPollTimerRef.current) {
      clearTimeout(aiPollTimerRef.current);
      aiPollTimerRef.current = null;
    }
    aiPollCountRef.current = 0;

    if (!isAIThinking) return;

    const q = debouncedQuery.trim();
    const POLL_INTERVALS = [3000, 5000, 8000, 12000];
    const key = searchKey(q, filters);
    const pageSize = filters.limit || PAGE_SIZE;
    const pageOneLimit = restoreRef.current?.key === key ? restoreRef.current.limit : pageSize;

    function schedulePoll() {
      if (aiPollCountRef.current >= POLL_INTERVALS.length) return;
      const delay = POLL_INTERVALS[aiPollCountRef.current];
      aiPollTimerRef.current = setTimeout(async () => {
        aiPollCountRef.current += 1;
        try {
          // Bypass client cache
          const cacheKey = `${key}|off=0|lim=${pageOneLimit}${exactMatch ? '|exact' : ''}`;
          searchCacheRef.current.delete(cacheKey);

          const data = await searchProducts({
            q,
            category: filters.category,
            type: filters.type,
            country: filters.country,
            region: filters.region,
            grape: filters.grape,
            producer: filters.producer,
            body: filters.body,
            acidity: filters.acidity,
            tannins: filters.tannins,
            fruitiness: filters.fruitiness,
            minPrice: filters.minPrice,
            maxPrice: filters.maxPrice,
            inStock: filters.inStock,
            sort: filters.sort,
            limit: pageOneLimit,
            offset: 0,
            sessionId: getSessionId(),
            lang,
            exact: exactMatch || undefined,
          });

          if (data.aiContext?.source === 'ai') {
            // AI result arrived. The backend re-ranks products using the AI's
            // suggested grapes/categories, so adopt the full re-ranked payload —
            // otherwise the pre-AI keyword ranking (e.g. Pinots for "τι κρασί
            // πίνω με θαλασσινά") stays on screen under a correct banner.
            // Only safe while still on page 1: the poll covers offset 0 only.
            if (currentPageRef.current === 1) {
              setAccumulatedProducts(data.products);
              setResults({ ...data });
            } else {
              setResults(prev => prev ? { ...prev, aiContext: data.aiContext } : prev);
            }
            searchCacheRef.current.set(cacheKey, { data, ts: Date.now() });
            return; // stop polling
          }
        } catch {
          // Ignore poll errors
        }
        schedulePoll();
      }, delay);
    }
    schedulePoll();

    return () => {
      if (aiPollTimerRef.current) {
        clearTimeout(aiPollTimerRef.current);
        aiPollTimerRef.current = null;
      }
    };
  }, [isAIThinking, debouncedQuery, filters, lang, exactMatch]);

  /* ---- Actions ---- */

  const setQuery = useCallback((q: string) => {
    setQueryRaw(q);
    setExactMatch(false);
    if (q.trim().length === 0) {
      setResults(null);
      setAutocomplete([]);
      setActiveIndex(-1);
    }
  }, []);

  const setFilters = useCallback((f: SearchFilters) => {
    setFiltersRaw((prev) => {
      // GA4: report newly-set/changed filter values (not removals)
      for (const [k, v] of Object.entries(f)) {
        if (v !== undefined && v !== '' && (prev as Record<string, unknown>)[k] !== v) {
          ga4Event('search_filter', { filter_name: k, filter_value: String(v) });
        }
      }
      return f;
    });
  }, []);

  const clearFilters = useCallback(() => {
    setFiltersRaw({});
  }, []);

  const applyBrowseFilters = useCallback((newFilters: Partial<SearchFilters>) => {
    setQueryRaw('');
    setExactMatch(false);
    setAutocomplete([]);
    setActiveIndex(-1);
    setFiltersRaw((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const open = useCallback(() => setIsOpen(true), []);

  const restoreSearchState = useCallback(
    (state: RestoreInput) => {
      const q = state.query.trim();
      const snap = state.snapshot ?? null;
      const loaded = snap ? snap.results.products.length : state.loadedCount;
      const restore = restoreLimitFor(q, state.filters, loaded);
      restoreRef.current = restore;
      hasClickedRef.current = true; // returning from a result isn't abandonment
      lastSearchQueryRef.current = q;
      if (snap) {
        const limit = restore?.limit ?? (state.filters.limit || PAGE_SIZE);
        const cacheKey = `${searchKey(q, state.filters)}|off=0|lim=${limit}`;
        searchCacheRef.current.set(cacheKey, { data: snap.results, ts: snap.ts });
        ga4FiredKeyRef.current = cacheKey;
        resultsKeyRef.current = searchKey(q, state.filters);
        lastSearchResultsRef.current = snap.results.total;
        setResults(snap.results);
        setAccumulatedProducts(snap.results.products);
        setHasMore(snap.results.products.length < snap.results.total);
      }
      setQueryRaw(state.query);
      setExactMatch(false);
      setDebouncedQuery(state.query); // skip the debounce: paint now
      setFiltersRaw(state.filters);
      setAutocomplete([]);
      setActiveIndex(-1);
      setError(null);
      setIsOpen(true);
    },
    [],
  );

  const close = useCallback(() => {
    const q = lastSearchQueryRef.current;
    const totalResults = lastSearchResultsRef.current;
    if (q && totalResults > 0 && !hasClickedRef.current) {
      // Mark as handled immediately to prevent duplicate events if close is called twice
      hasClickedRef.current = true;
      fetch(`${getApiBase()}/analytics/abandon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ q, results: totalResults }),
        keepalive: true,
      }).catch(() => { /* fire-and-forget */ });
    }
    setIsOpen(false);
    setActiveIndex(-1);
  }, []);

  const addToRecentSearches = useCallback(
    (term: string) => {
      const trimmed = term.trim();
      if (!trimmed) return;
      setRecentSearches((prev) => {
        const updated = [
          trimmed,
          ...prev.filter((s) => s.toLowerCase() !== trimmed.toLowerCase()),
        ].slice(0, MAX_RECENT_SEARCHES);
        saveRecentSearches(updated, lang);
        return updated;
      });
    },
    [lang],
  );

  const selectSuggestion = useCallback(
    (suggestion: string) => {
      hasClickedRef.current = true; // user acted on a result — not abandonment
      setQueryRaw(suggestion);
      setExactMatch(false);
      setAutocomplete([]);
      setActiveIndex(-1);
      addToRecentSearches(suggestion);
    },
    [addToRecentSearches],
  );

  const searchExact = useCallback(() => setExactMatch(true), []);

  const clearQuery = useCallback(() => {
    setQueryRaw('');
    setExactMatch(false);
    setResults(null);
    setAutocomplete([]);
    setActiveIndex(-1);
    setError(null);
    setFiltersRaw({});
  }, []);

  const clearRecentSearches = useCallback(() => {
    setRecentSearches([]);
    saveRecentSearches([], lang);
  }, [lang]);

  // Keep the latest query/results for the fire-and-forget click logger below,
  // so it records the real query + position without a stale closure.
  const queryRef = useRef(query);
  queryRef.current = query;
  const resultsRef = useRef(results);
  resultsRef.current = results;

  const trackProductClick = useCallback((product: Product) => {
    hasClickedRef.current = true;
    setRecentProductIds((prev) => {
      const updated = addRecentProductId(prev, product.id);
      saveRecentProductIds(getLocalStorageSafe(), updated);
      return updated;
    });
    setRecentProducts((prev) =>
      [product, ...prev.filter((p) => p.id !== product.id)].slice(0, MAX_RECENT_PRODUCTS),
    );
    // Send click to backend for session-based personalization + analytics CTR.
    const q = queryRef.current.trim();
    const pos = resultsRef.current?.products.findIndex((p) => p.id === product.id) ?? -1;
    ga4SelectItem(product, pos >= 0 ? pos : undefined);
    trackSessionClick({
      sessionId: getSessionId(),
      productId: product.id,
      category: product.categories?.[0],
      producer: product.producer,
      region: product.region,
      price: product.salePrice ?? product.price,
      query: q || undefined,
      position: pos >= 0 ? pos : undefined,
      lang,
    });
  }, [lang]);

  const toggleSidebar = useCallback(() => {
    setShowSidebar((prev) => {
      const next = !prev;
      saveSidebarVisibility(next);
      return next;
    });
  }, []);

  const loadMore = useCallback(() => {
    if (!isLoadingMore && hasMore) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [isLoadingMore, hasMore]);

  /* ---- Keyboard navigation ---- */

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      /* Navigable list: autocomplete if visible, otherwise result count */
      const listLength = showAutocomplete
        ? autocomplete.length
        : results
          ? results.products.length +
            results.articles.length +
            results.pages.length
          : 0;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setActiveIndex((prev) =>
            prev < listLength - 1 ? prev + 1 : 0,
          );
          break;

        case 'ArrowUp':
          e.preventDefault();
          setActiveIndex((prev) =>
            prev > 0 ? prev - 1 : listLength - 1,
          );
          break;

        case 'Enter':
          e.preventDefault();
          if (
            showAutocomplete &&
            activeIndex >= 0 &&
            activeIndex < autocomplete.length
          ) {
            selectSuggestion(autocomplete[activeIndex]);
          } else if (trimmedQuery) {
            addToRecentSearches(trimmedQuery);
          }
          break;

        case 'Escape':
          e.preventDefault();
          close();
          break;

        default:
          break;
      }
    },
    [
      showAutocomplete,
      autocomplete,
      results,
      activeIndex,
      trimmedQuery,
      selectSuggestion,
      addToRecentSearches,
      close,
    ],
  );

  return {
    query,
    results,
    defaults,
    autocomplete,
    isLoading,
    isLoadingMore,
    isLoadingDefaults,
    error,
    isOpen,
    activeIndex,
    recentSearches,
    recentProducts,
    filters,
    showAutocomplete,
    showResults,
    showDefaults,
    showNoResults,
    showSidebar,
    hasMore,
    currentPage,
    isAIThinking,
    setQuery,
    setFilters,
    clearFilters,
    applyBrowseFilters,
    open,
    close,
    selectSuggestion,
    clearQuery,
    clearRecentSearches,
    trackProductClick,
    handleKeyDown,
    toggleSidebar,
    loadMore,
    searchExact,
    restoreSearchState,
  };
}
